import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useAuthStore } from '../auth/useAuthStore';
import { usePortalAccess } from './PortalAccessContext';
import { findAnalysisLabel } from './analyses';
import { clientPortalApi, MUNICIPAL_BANDS, type LineCoverageSummary } from './api';
import './ficha.css';
import { FichaTerritorialPanel } from './FichaTerritorialPanel';
import { splitRouteRuns, type LngLat } from './routeGeo';
import { formatKm, formatPct, formatPopulation } from './summaryFormat';
import streetRoutesData from './streetRoutes.json';

// Asset servido desde `public/` (Vite lo copia tal cual, sin procesar vía
// import) — versión color de la marca (no la "negativa" blanca, que
// necesitaba un chip oscuro de fondo para tener contraste).
const SINTAX_LOGO_SRC = '/assets/brand/sintax-color.svg';

const MAPTILER_KEY = (import.meta.env.VITE_MAPTILER_API_KEY as string | undefined) ?? '';

type LoadState = 'loading' | 'error' | 'ready';

interface StreetRouteEntry {
  mode: 'CIRCUIT' | 'IDAVUELTA';
  ida?: string;
  vuelta?: string | null;
  circuito?: string;
  geometryReviewNote?: string;
}

// Recorrido calle-por-calle — fuente documental aprobada, auditada y
// resuelta manualmente. NO se recalcula acá: este archivo es una copia
// estática de route-builder-workspace/osm-street-names/
// RECORRIDOS-21-ENTIDADES-FINAL.md. Para actualizarlo, regenerar ese
// documento y volver a copiar — nunca editar a mano ni inferir nombres
// nuevos desde este componente.
// `resolveJsonModule` infiere `mode` como `string` genérico (no como el
// union literal `'CIRCUIT' | 'IDAVUELTA'`) — el dato en sí es válido y
// estático, es solo una limitación de inferencia de TS sobre JSON.
const STREET_ROUTES: Record<string, StreetRouteEntry> = streetRoutesData as unknown as Record<
  string,
  StreetRouteEntry
>;

function FichaStreetRoute({ analysisId }: { analysisId: string }): JSX.Element | null {
  const entry = STREET_ROUTES[analysisId];
  if (!entry) return null;

  return (
    <section className="ficha-street-route">
      <h3 className="ficha-street-route-title">Recorrido</h3>
      {entry.geometryReviewNote && (
        <p className="ficha-street-route-note">{entry.geometryReviewNote}</p>
      )}
      {entry.mode === 'CIRCUIT' ? (
        <div className="ficha-street-route-block">
          <span className="ficha-street-route-label">CIRCUITO</span>
          <p className="ficha-street-route-text">{entry.circuito}</p>
        </div>
      ) : (
        <>
          <div className="ficha-street-route-block">
            <span className="ficha-street-route-label">IDA</span>
            <p className="ficha-street-route-text">{entry.ida}</p>
          </div>
          {entry.vuelta && (
            <div className="ficha-street-route-block">
              <span className="ficha-street-route-label">VUELTA</span>
              <p className="ficha-street-route-text">{entry.vuelta}</p>
            </div>
          )}
        </>
      )}
    </section>
  );
}

interface RouteShapesBundle {
  routeShapesTechnical: {
    IDA: { coordinates: LngLat[] };
    VUELTA: { coordinates: LngLat[] };
  };
}

function lineFeature(coords: LngLat[]): GeoJSON.Feature<GeoJSON.LineString> {
  return { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords } };
}

function multiLineFeature(runs: LngLat[][]): GeoJSON.Feature<GeoJSON.MultiLineString> {
  return { type: 'Feature', properties: {}, geometry: { type: 'MultiLineString', coordinates: runs } };
}

/** Triangulo simple dibujado a mano en un canvas — un solo ícono reusado
 * por las 3 capas de flechas, tinte via `icon-color` (SDF). */
function buildArrowIcon(): { width: number; height: number; data: Uint8ClampedArray } {
  const size = 24;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#000';
  ctx.beginPath();
  ctx.moveTo(4, 4);
  ctx.lineTo(21, 12);
  ctx.lineTo(4, 20);
  ctx.closePath();
  ctx.fill();
  const { data } = ctx.getImageData(0, 0, size, size);
  // `addImage` exige RGBA (4 bytes/píxel) aunque la imagen sea SDF — un
  // buffer de 1 byte/píxel (solo alpha) queda con la forma equivocada y
  // MapLibre no la dibuja. SDF real requeriría distance-transform; para un
  // triángulo chico alcanza replicar el alpha en los 4 canales (borde
  // duro, sin degradé, pero nítido y liviano).
  const rgba = new Uint8ClampedArray(size * size * 4);
  for (let i = 0; i < size * size; i++) {
    const a = data[i * 4 + 3];
    rgba[i * 4] = a;
    rgba[i * 4 + 1] = a;
    rgba[i * 4 + 2] = a;
    rgba[i * 4 + 3] = a;
  }
  return { width: size, height: size, data: rgba };
}

/**
 * Mapa real (MapLibre + calles de fondo CARTO, gratis y sin API key) en vez
 * del SVG de proyección propia anterior — sin mapa de calles de fondo no
 * se entendía por dónde pasaba el recorrido en los zigzags. `interactive:
 * false` porque esto es un documento, no un visor.
 */
function FichaMap({
  ida,
  vuelta,
  isCircuit,
}: {
  ida: LngLat[];
  vuelta: LngLat[];
  isCircuit: boolean;
}): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || ida.length < 2) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          basemap: {
            type: 'raster',
            // Los tiles raster "gratis" de CARTO (basemaps.cartocdn.com) ahora
            // devuelven un watermark "API KEY REQUIRED" sin autenticación —
            // política cambiada. Se usa MapTiler con la key ya configurada en
            // el portal (VITE_MAPTILER_API_KEY, mismo patrón que el resto del
            // proyecto: VsjMapSection, MapStyleService, etc.).
            // "positron" = estilo claro/minimalista (equivalente al viejo
            // CARTO Positron que se buscaba originalmente) — fondo casi
            // blanco, calles en gris suave, no compite visualmente con las
            // líneas de recorrido de colores.
            tiles: [`https://api.maptiler.com/maps/positron/256/{z}/{x}/{y}.png?key=${MAPTILER_KEY}`],
            tileSize: 256,
            attribution: '&copy; MapTiler &copy; OpenStreetMap contributors',
          },
        },
        layers: [{ id: 'basemap', type: 'raster', source: 'basemap' }],
      },
      interactive: false,
      attributionControl: false,
    });
    containerRef.current.dataset.mapReady = 'false';
    // Registrado ANTES de `load`: si el contexto WebGL falla al crearse
    // (típico en Chrome headless sin banderas de software rendering),
    // `load` nunca dispara y un listener de error solo-adentro-de-load no
    // se llega a registrar — así queda una señal en el DOM igual.
    map.on('error', ev => {
      if (containerRef.current) containerRef.current.dataset.mapError = String(ev.error?.message ?? ev.error ?? 'unknown');
    });

    map.on('load', () => {
      const allCoords = isCircuit ? ida : [...ida, ...vuelta];
      const bounds = allCoords.reduce(
        (b, c) => b.extend(c as [number, number]),
        new maplibregl.LngLatBounds(allCoords[0] as [number, number], allCoords[0] as [number, number])
      );
      map.fitBounds(bounds, { padding: 36, duration: 0 });

      map.addSource('route-ida', { type: 'geojson', data: lineFeature(ida) });
      map.addLayer({
        id: 'l-route-ida',
        type: 'line',
        source: 'route-ida',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#e5622e', 'line-width': 4 },
      });
      if (!isCircuit) {
        map.addSource('route-vuelta', { type: 'geojson', data: lineFeature(vuelta) });
        map.addLayer({
          id: 'l-route-vuelta',
          type: 'line',
          source: 'route-vuelta',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#1c6fd6', 'line-width': 3, 'line-dasharray': [2, 1.4] },
        });
      }

      // Densidad de flechas controlada por MapLibre según zoom
      // (`symbol-placement: 'line'`, espaciado en PÍXELS de pantalla, no
      // metros reales) — a diferencia de un espaciado fijo en metros, esto
      // da pocas flechas en la vista general y más al acercarse, sin
      // convertirse nunca en una fila continua de triángulos. Los tramos
      // se agrupan primero (`splitRouteRuns`) para que un giro corto entre
      // dos avenidas largas no reciba flecha propia.
      const { idaLines, vueltaLines, sharedLines } = splitRouteRuns(ida, vuelta, isCircuit);
      map.addImage('ficha-arrow-icon', buildArrowIcon(), { sdf: true });
      const arrowLayers: Array<[string, LngLat[][], string]> = [
        ['ida', idaLines, '#e5622e'],
        ['vuelta', vueltaLines, '#1c6fd6'],
        ['twoway', sharedLines, '#6b3fa0'],
      ];
      for (const [key, runs, color] of arrowLayers) {
        if (runs.length === 0) continue;
        map.addSource(`arrows-${key}`, { type: 'geojson', data: multiLineFeature(runs) });
        map.addLayer({
          id: `l-arrows-${key}`,
          type: 'symbol',
          source: `arrows-${key}`,
          layout: {
            'symbol-placement': 'line',
            'symbol-spacing': 170,
            'icon-image': 'ficha-arrow-icon',
            'icon-rotation-alignment': 'map',
            'icon-allow-overlap': true,
            'icon-size': 0.5,
          },
          paint: { 'icon-color': color, 'icon-halo-color': '#fff', 'icon-halo-width': 1 },
        });
      }

      // `idle` puede demorar de más (o no disparar en un tiempo acotado):
      // MapLibre sigue pidiendo tiles de niveles de zoom vecinos en
      // segundo plano para el pyramid cache aunque la vista visible ya
      // esté completa. El export a PDF necesita una señal de "listo" con
      // cota de tiempo — se toma la primera señal entre `idle` real y un
      // fallback fijo (la vista visible, que es lo único que importa para
      // el PDF, ya está pintada bastante antes de eso).
      let readyFired = false;
      const markReady = (): void => {
        if (readyFired) return;
        readyFired = true;
        if (containerRef.current) containerRef.current.dataset.mapReady = 'true';
      };
      map.once('idle', markReady);
      window.setTimeout(markReady, 2500);
    });

    return (): void => {
      map.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ida/vuelta son arrays nuevos por render; se remonta el mapa una sola vez por analysisId (el componente padre lo desmonta al cambiar de línea)
  }, []);

  return (
    <div className="ficha-map-canvas-wrap">
      <div
        ref={containerRef}
        className="ficha-map-canvas"
        role="img"
        aria-label={isCircuit ? 'Trazado del circuito sobre el mapa de calles' : 'Trazado IDA y VUELTA sobre el mapa de calles'}
      />
      <p className="ficha-map-attribution">&copy; MapTiler &copy; OpenStreetMap contributors</p>
    </div>
  );
}

function IndicatorCard({ label, value, dual }: { label: string; value: string; dual?: boolean }): JSX.Element {
  return (
    <div className="ficha-indicator-row">
      <p className={dual ? 'ficha-indicator-value ficha-indicator-value-dual' : 'ficha-indicator-value'}>{value}</p>
      <p className="ficha-indicator-label">{label}</p>
    </div>
  );
}

// --- Agregación por banda ------------------------------------------------
// La ficha es un documento de la LÍNEA, no de una banda horaria puntual:
// se consultan las 6 bandas municipales y se reducen a rango (min–max) o
// valor único cuando no varía. La flota es la única magnitud que se
// reporta como máximo diario (nunca "de una banda"), igual que ya hacía
// `maxFleetRequired` en el resto del portal.
interface BandRow {
  start: string;
  end: string;
  frequencyMin: number | null;
  cycleMin: number | null;
  fleetRequired: number | null;
}

interface FichaAggregate {
  label: string;
  routeMode: 'CIRCUIT' | null;
  idaKm: number;
  vueltaKm: number;
  idaTimeRange: [number, number] | null;
  vueltaTimeRange: [number, number] | null;
  cycleRange: [number, number] | null;
  maxFleetRequired: number | null;
  coverage2min: LineCoverageSummary | null;
  coverage5min: LineCoverageSummary | null;
  configSource: 'saved' | 'default';
  perBand: BandRow[];
}

function numRange(values: Array<number | null>): [number, number] | null {
  const nums = values.filter((v): v is number => v !== null && !Number.isNaN(v));
  if (nums.length === 0) return null;
  return [Math.min(...nums), Math.max(...nums)];
}

function formatMinRange(range: [number, number] | null): string {
  if (!range) return '—';
  const [min, max] = [Math.round(range[0]), Math.round(range[1])];
  return min === max ? `${min} min` : `${min}–${max} min`;
}

// Longitud representativa de la línea para relativizar la cobertura
// territorial ("población cubierta por km"). En CIRCUIT hay una sola
// geometría (el circuito). En IDA/VUELTA se promedia IDA y VUELTA en vez de
// sumarlas: la mayoría de las líneas recorren en gran parte el mismo
// corredor en ambos sentidos, así que sumar duplicaría esa extensión y
// subestimaría artificialmente la cobertura por km. El promedio da una
// longitud representativa del corredor, comparable con el largo único de
// un circuito — misma definición para las 21 entidades.
function representativeKm(data: FichaAggregate): number {
  return data.routeMode === 'CIRCUIT' ? data.idaKm : (data.idaKm + data.vueltaKm) / 2;
}

function formatCoveragePerKm(data: FichaAggregate): string {
  const km = representativeKm(data);
  if (!data.coverage5min || !km) return '—';
  return `${formatPopulation(Math.round(data.coverage5min.populationCovered / km))} hab./km`;
}

function useFichaData(
  slug: string,
  token: string | null,
  analysisId: string
): { data: FichaAggregate | null; ida: LngLat[]; vuelta: LngLat[]; loadState: LoadState } {
  const [data, setData] = useState<FichaAggregate | null>(null);
  const [ida, setIda] = useState<LngLat[]>([]);
  const [vuelta, setVuelta] = useState<LngLat[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('loading');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoadState('loading');

    Promise.all([
      Promise.all(
        MUNICIPAL_BANDS.map(band => clientPortalApi.getAnalysisSummary(slug, token, 'Hábil', band))
      ),
      clientPortalApi.getAnalysisBundle(slug, analysisId, token),
    ])
      .then(([summaries, bundle]) => {
        if (cancelled) return;

        // La ficha se pide por analysisId de la service entity puntual — para
        // variantes (503-Corto, 507-Espora, 517/519A-Rondín) esa entidad no
        // tiene fila propia en `summary.lines` (solo las 17 comerciales),
        // vive anidada en `row.variants`. Buscar en ambos niveles.
        const rows = summaries.map(
          s =>
            s.lines.find(l => l.analysisId === analysisId) ??
            s.lines.flatMap(l => l.variants).find(v => v.analysisId === analysisId) ??
            null
        );
        const first = rows.find(r => r !== null) ?? null;

        if (!first) {
          setLoadState('error');
          return;
        }

        const perBand: BandRow[] = MUNICIPAL_BANDS.map((band, i) => ({
          start: band.start,
          end: band.end,
          frequencyMin: rows[i]?.frequencyMin ?? null,
          cycleMin: rows[i]?.cycleMin ?? null,
          fleetRequired: rows[i]?.fleetRequired ?? null,
        }));

        const aggregate: FichaAggregate = {
          label: first.label,
          routeMode: first.routeMode,
          idaKm: first.idaKm,
          vueltaKm: first.vueltaKm,
          idaTimeRange: numRange(rows.map(r => r?.idaTimeMin ?? null)),
          vueltaTimeRange: numRange(rows.map(r => r?.vueltaTimeMin ?? null)),
          cycleRange: numRange(perBand.map(b => b.cycleMin)),
          maxFleetRequired: first.maxFleetRequired,
          coverage2min: first.coverage2min,
          coverage5min: first.coverage5min,
          configSource: first.configSource,
          perBand,
        };

        const shapes = (bundle as RouteShapesBundle).routeShapesTechnical;
        setData(aggregate);
        setIda(shapes.IDA.coordinates);
        setVuelta(shapes.VUELTA.coordinates);
        setLoadState('ready');
      })
      .catch(() => {
        if (!cancelled) setLoadState('error');
      });

    return (): void => {
      cancelled = true;
    };
  }, [slug, token, analysisId]);

  return { data, ida, vuelta, loadState };
}

// Síntesis analítica de la línea, no un dashboard de KPIs: 7 indicadores
// como máximo, agrupados en 3 familias conceptuales (recorrido / operación
// / alcance territorial) — todo lo que ya se detalla más abajo (frecuencia
// por banda, cobertura 2 min, paradas) se resume acá en una sola vez o se
// traslada a una nota secundaria, nunca se repite en una tarjeta propia.
function FichaIndicators({ data }: { data: FichaAggregate }): JSX.Element {
  const isCircuit = data.routeMode === 'CIRCUIT';
  return (
    <div className="ficha-indicators-summary">
      <div className="ficha-indicator-group">
        <p className="ficha-indicator-group-title">Recorrido</p>
        {isCircuit ? (
          <>
            <IndicatorCard label="Longitud del circuito" value={formatKm(data.idaKm)} />
            <IndicatorCard label="Tiempo estimado del circuito" value={formatMinRange(data.idaTimeRange)} />
          </>
        ) : (
          <>
            <IndicatorCard
              label="Longitud del recorrido"
              value={`IDA ${formatKm(data.idaKm)} · VUELTA ${formatKm(data.vueltaKm)}`}
              dual
            />
            <IndicatorCard
              label="Tiempo estimado de viaje"
              value={`IDA ${formatMinRange(data.idaTimeRange)} · VUELTA ${formatMinRange(data.vueltaTimeRange)}`}
              dual
            />
          </>
        )}
      </div>

      <div className="ficha-indicator-group">
        <p className="ficha-indicator-group-title">Operación</p>
        <IndicatorCard label="Ciclo operativo" value={formatMinRange(data.cycleRange)} />
        <IndicatorCard
          label="Flota máxima requerida"
          value={data.maxFleetRequired !== null ? `${data.maxFleetRequired} unidades` : '—'}
        />
      </div>

      <div className="ficha-indicator-group">
        <p className="ficha-indicator-group-title">Alcance territorial</p>
        <IndicatorCard
          label="Población a ≤5 min a pie"
          value={data.coverage5min ? formatPct(data.coverage5min.populationCoveragePct) : '—'}
        />
        <IndicatorCard
          label="Población cubierta a ≤5 min"
          value={data.coverage5min ? `${formatPopulation(data.coverage5min.populationCovered)} hab.` : '—'}
        />
        <IndicatorCard label="Cobertura por km" value={formatCoveragePerKm(data)} />
      </div>
    </div>
  );
}

function FichaSecondaryNote({ data }: { data: FichaAggregate }): JSX.Element {
  const parts: string[] = [];
  if (data.coverage2min) {
    parts.push(`Cobertura peatonal a 2 minutos: ${formatPct(data.coverage2min.populationCoveragePct)}.`);
  }
  if (data.configSource !== 'saved') {
    parts.push('La asociación definitiva de paradas se encuentra pendiente de validación.');
  }
  if (parts.length === 0) return <></>;
  return <p className="ficha-secondary-note">{parts.join(' ')}</p>;
}

function FichaBandTable({ perBand }: { perBand: BandRow[] }): JSX.Element {
  return (
    <table className="ficha-band-table">
      <thead>
        <tr>
          <th>Banda horaria</th>
          <th>Frecuencia</th>
          <th>Ciclo</th>
          <th>Unidades</th>
        </tr>
      </thead>
      <tbody>
        {perBand.map(b => (
          <tr key={b.start}>
            <td>
              {b.start}–{b.end}
            </td>
            <td>{b.frequencyMin !== null ? `${b.frequencyMin} min` : '—'}</td>
            <td>{b.cycleMin !== null ? `${Math.round(b.cycleMin)} min` : '—'}</td>
            <td>{b.fleetRequired !== null ? b.fleetRequired : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * `/analysis/:analysisId/ficha` — ficha técnica de una línea (documento de
 * ruta, no de una banda horaria puntual), pensada para imprimir/exportar a
 * PDF vía `window.print()` del navegador (fondo claro, sin el chrome
 * oscuro del visor). El mapa usa MapLibre GL con basemap real de MapTiler
 * (estilo "positron", claro) y flechas de sentido propias sobre las
 * coordenadas geográficas — ver `FichaMap` y `routeGeo.ts`.
 */
export function FichaPage(): JSX.Element {
  const { slug } = usePortalAccess();
  const { analysisId } = useParams<{ analysisId: string }>();
  const token = useAuthStore(state => state.token);

  const label = analysisId ? findAnalysisLabel(analysisId) : '';
  const { data, ida, vuelta, loadState } = useFichaData(slug, token, analysisId ?? '');

  // El portal declara, en :root (ver styles/index.css): `color-scheme: dark`
  // Y ADEMÁS `background-color: var(--bg-primary)` (oscuro) explícito sobre
  // el propio <html>. Los dos pintaban de negro distintas zonas del PDF:
  // color-scheme afectaba el margen de @page, y el background-color de
  // <html> quedaba visible en el resto de la última página, donde
  // `.ficha-sheet` ya terminó pero la hoja física sigue — ahí no hay
  // ningún elemento propio de la ficha que lo cubra, así que se veía el
  // fondo oscuro de <html> por debajo. La ficha es un documento claro
  // siempre: se fuerzan los tres (color-scheme, background de <html> y de
  // <body>) mientras esta ruta está montada, y se restauran los valores
  // previos al salir — nunca se toca el tema oscuro del resto del portal.
  useEffect(() => {
    const root = document.documentElement;
    const prevColorScheme = root.style.colorScheme;
    const prevRootBackground = root.style.backgroundColor;
    const prevBodyBackground = document.body.style.backgroundColor;
    root.style.colorScheme = 'light';
    root.style.backgroundColor = '#fff';
    document.body.style.backgroundColor = '#fff';
    return (): void => {
      root.style.colorScheme = prevColorScheme;
      root.style.backgroundColor = prevRootBackground;
      document.body.style.backgroundColor = prevBodyBackground;
    };
  }, []);

  if (!analysisId || loadState === 'error') {
    return (
      <div className="portal-denied">
        <h1>No se pudo generar la ficha</h1>
        <Link to="/analysis">Volver a Sistema de Transporte</Link>
      </div>
    );
  }

  if (loadState === 'loading' || !data) {
    return <div className="portal-loading">Generando ficha {label}…</div>;
  }

  const isCircuit = data.routeMode === 'CIRCUIT';

  return (
    <div className="ficha-page">
      <div className="ficha-toolbar">
        <div className="ficha-toolbar-links">
          <Link to={`/analysis/${analysisId}`}>← Volver a {label}</Link>
          <Link to="/analysis">Sistema de Transporte</Link>
        </div>
        <button
          onClick={() => {
            window.print();
          }}
        >
          Imprimir / Exportar PDF
        </button>
      </div>

      <div className="ficha-sheet">
        <header className="ficha-header">
          <p className="ficha-eyebrow">Bahía Blanca · Sistema de transporte público</p>
          <h1 className="ficha-title">{data.label}</h1>
          <p className="ficha-subtitle">{data.routeMode === 'CIRCUIT' ? 'CIRCUITO' : 'IDA / VUELTA'}</p>
        </header>

        <div className="ficha-map">
          <FichaMap ida={ida} vuelta={vuelta} isCircuit={isCircuit} />
          <div className="ficha-map-legend">
            {isCircuit ? (
              <span>
                <i className="ficha-legend-dot ficha-legend-ida" /> Sentido de circulación
              </span>
            ) : (
              <>
                <span>
                  <i className="ficha-legend-dot ficha-legend-ida" /> IDA
                </span>
                <span>
                  <i className="ficha-legend-dot ficha-legend-vuelta" /> VUELTA
                </span>
                <span>
                  <i className="ficha-legend-dot ficha-legend-twoway" /> Doble sentido (misma calle)
                </span>
              </>
            )}
          </div>
        </div>

        <FichaIndicators data={data} />
        <FichaSecondaryNote data={data} />

        <div className="ficha-band-detail">
          <h4 className="ficha-band-detail-title">Programación por banda horaria</h4>
          <FichaBandTable perBand={data.perBand} />
        </div>

        <FichaStreetRoute analysisId={analysisId} />

        <div className="ficha-methodology">
          <h4 className="ficha-methodology-title">Metodología</h4>
          <p className="ficha-methodology-note">
            Tiempos de viaje estimados a partir de velocidades informadas por el Municipio y
            valores complementarios cuando fue necesario. La frecuencia, el tiempo de ciclo y la
            flota requerida se determinan a partir de la programación prevista para la línea. La
            cobertura peatonal representa la población con acceso al corredor a 2 y 5 minutos a
            pie.
          </p>
        </div>

        <footer className="ficha-footer">
          <img src={SINTAX_LOGO_SRC} alt="Sintax" className="ficha-brand-logo" />
        </footer>
      </div>

      {token && (
        <FichaTerritorialPanel
          slug={slug}
          token={token}
          analysisId={analysisId}
          routeMode={data.routeMode}
          ida={ida as [number, number][]}
          vuelta={vuelta as [number, number][]}
        />
      )}
    </div>
  );
}
